"use client";

import { useEffect } from "react";

import { trackEvent } from "@/lib/posthog";

/**
 * Scroll-reveal driver for the .vrx marketing pages. Content is fully
 * visible without JS (no-JS visitors and crawlers see everything); on mount
 * we hide only below-fold .reveal elements (.pre) and reveal them (.in) as
 * they enter the viewport. Above-fold elements are never hidden, so there's
 * no flash on load.
 */
export function RevealObserver() {
  useEffect(() => {
    const els = document.querySelectorAll<HTMLElement>(".vrx .reveal");
    if (els.length === 0) return;

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add("in");
            io.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.12 },
    );

    for (const el of els) {
      if (el.getBoundingClientRect().top < window.innerHeight) continue;
      el.classList.add("pre");
      io.observe(el);
    }

    // Animated .demo mockups: arm (hide bubbles) only when JS is running and
    // motion is allowed, then .play staggers them in on intersect. No-JS and
    // reduced-motion visitors keep the fully-visible static scene.
    let dio: IntersectionObserver | undefined;
    const demos = document.querySelectorAll<HTMLElement>(".vrx .demo");
    if (
      demos.length > 0 &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      dio = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              entry.target.classList.add("play");
              dio!.unobserve(entry.target);
            }
          }
        },
        { threshold: 0.4 },
      );
      for (const el of demos) {
        el.classList.add("armed");
        dio.observe(el);
      }
    }

    // Named conversion events for the register CTAs (autocapture already
    // records the clicks, but a stable event name makes signup funnels
    // trivial to build). Delegated: covers every CTA on the page, nav pill
    // included, with zero per-CTA wiring.
    const onCtaClick = (e: MouseEvent) => {
      const a = (e.target as HTMLElement).closest?.(
        'a[href^="/login?mode=register"]',
      );
      if (!a) return;
      trackEvent("marketing_signup_cta_clicked", {
        page: window.location.pathname,
        cta_text: (a.textContent || "").trim().slice(0, 60),
      });
    };
    document.addEventListener("click", onCtaClick, true);

    return () => {
      io.disconnect();
      dio?.disconnect();
      document.removeEventListener("click", onCtaClick, true);
    };
  }, []);

  return null;
}
